import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { formatPln } from '../catalog/money';
import { formatEventRange } from '../events/labels';
import { IssueInvoiceError, useIssueInvoice } from './IssueInvoiceButton';
import { KsefBadge, waitingForKsef } from './KsefBadge';
import { RowMenu } from '../ui/RowMenu';
import { dayText, INVOICES_KEY, INVOICES_RESOURCE, type InvoiceListItem, type ToIssueRow } from './types';
import { planShareNote } from './planShare';
import './invoices.css';
import { Notice } from '../ui/Notice';

type StateFilter = '' | 'robocza' | 'wystawiona' | 'zewnetrzna' | 'do_wystawienia';
const STATES: Array<{ id: StateFilter; label: string }> = [
  { id: '', label: 'Wszystkie' },
  { id: 'robocza', label: 'Robocze' },
  { id: 'wystawiona', label: 'Wystawione' },
  // F41: sales invoices issued outside the program, downloaded from KSeF
  { id: 'zewnetrzna', label: 'Spoza programu' },
];

/**
 * "Finanse → Faktury": the sales invoices, newest first; refreshed every 5 s while some wait for KSeF. The tab
 * "Do wystawienia" (owner 2026-10-20 23:42) lists the finished events still to invoice: first, with their count, when
 * there are any; "Wszystkie" stays the default.
 */
export function InvoicesPage() {
  const navigate = useNavigate();
  const [month, setMonth] = useState('');
  // `?stan=do-wystawienia` ("Do zrobienia", F46) opens the tab "Do wystawienia"
  const [urlParams] = useSearchParams();
  const [state, setState] = useState<StateFilter>(urlParams.get('stan') === 'do-wystawienia' ? 'do_wystawienia' : '');
  const [q, setQ] = useState('');
  const listState = state === 'do_wystawienia' ? '' : state;
  const params = new URLSearchParams({ ...(month ? { month } : {}), ...(listState ? { state: listState } : {}), ...(q.trim() ? { q: q.trim() } : {}) });
  const list = useQuery({
    queryKey: [...INVOICES_KEY, 'list', month, listState, q.trim()],
    enabled: state !== 'do_wystawienia',
    queryFn: () => api<{ invoices: InvoiceListItem[]; truncated: boolean }>(`/api/invoices?${params.toString()}`),
    refetchInterval: (query) => (query.state.data?.invoices.some(waitingForKsef) ? 5000 : false),
  });
  const toIssue = useQuery({ queryKey: [...INVOICES_KEY, 'to-issue'], queryFn: () => api<{ events: ToIssueRow[]; outside: ToIssueRow[] }>('/api/invoices/to-issue') });
  const waiting = toIssue.data?.events.length ?? 0;
  const tabs: Array<{ id: StateFilter; label: string; count?: number }> = waiting > 0 ? [{ id: 'do_wystawienia', label: 'Do wystawienia', count: waiting }, ...STATES] : [...STATES, { id: 'do_wystawienia', label: 'Do wystawienia' }];
  const rows = list.data?.invoices ?? [];
  const amounts = rows.some((r) => r.grossGrosze !== undefined);
  const open = (id: string) => navigate(`/finanse/faktury/${id}`);
  return (
    <section className="page wide inv-page" aria-label="Faktury" data-feature-id="invoices.list">
      <div className="inv-filters">
        <div className="segment" role="group" aria-label="Stan faktur">
          {tabs.map((s) => (
            <button key={s.id} type="button" aria-pressed={state === s.id} onClick={() => setState(s.id)} {...(s.id === 'do_wystawienia' ? { 'data-feature-id': 'invoices.to_issue' } : {})}>
              {s.label}
              {s.count !== undefined && (
                <span className="count-badge" aria-label={`, ${s.count}`}>
                  {s.count}
                </span>
              )}
            </button>
          ))}
        </div>
        {state !== 'do_wystawienia' && (
          <>
            <label className="inv-filter">
              <span>Miesiąc</span>
              <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
            </label>
            <input className="inv-search" type="search" aria-label="Szukaj faktury" placeholder="Numer, nabywca, NIP, wydarzenie" value={q} onChange={(e) => setQ(e.target.value)} />
          </>
        )}
      </div>
      {state === 'do_wystawienia' ? <ToIssueList query={toIssue} /> : <InvoiceList list={list} rows={rows} amounts={amounts} open={open} />}
    </section>
  );
}

function InvoiceList({ list, rows, amounts, open }: { list: { error: unknown; data?: { truncated: boolean } | undefined }; rows: InvoiceListItem[]; amounts: boolean; open: (id: string) => void }) {
  return (
    <>
      {list.error && (
        <p className="alert" role="alert">
          {list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać faktur'}
        </p>
      )}
      {list.data && rows.length === 0 && <p className="muted">Brak faktur. Fakturę wystawia się z zaakceptowanej wersji kosztorysu (zakładka „Kosztorys” wydarzenia).</p>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table inv-table" aria-label="Faktury sprzedaży">
            <thead>
              <tr>
                <th scope="col">Numer</th>
                <th scope="col">Nabywca</th>
                <th scope="col" className="wide-only">
                  Wydarzenie
                </th>
                {amounts && (
                  <th scope="col" className="num">
                    Brutto
                  </th>
                )}
                <th scope="col" className="wide-only">
                  Termin
                </th>
                <th scope="col">KSeF</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="clickable" onClick={() => open(r.id)}>
                  <td className="nowrap">
                    <a
                      href={`/finanse/faktury/${r.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        open(r.id);
                      }}
                    >
                      {r.number ?? 'robocza'}
                    </a>
                  </td>
                  <td>{r.buyer.name || <span className="muted">bez nabywcy</span>}</td>
                  <td className="wide-only">{r.state === 'zewnetrzna' ? <span className="muted">spoza programu</span> : r.eventName}</td>
                  {amounts && <td className="num nowrap">{r.grossGrosze === undefined ? '—' : formatPln(r.grossGrosze)}</td>}
                  <td className="nowrap wide-only">{dayText(r.dueDate)}</td>
                  <td>
                    <KsefBadge invoice={r} />
                    {r.attention.length > 0 && (
                      <span className="todo-dot" role="img" aria-label={r.attention.map((a) => a.message).join(' ')} title={r.attention.map((a) => a.message).join(' ')} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {list.data?.truncated && <p className="muted">Pokazano 500 najnowszych faktur – zawęź miesiąc albo wyszukiwanie.</p>}
    </>
  );
}

/**
 * "Do wystawienia": event, dates, client, accepted version, the amount still to invoice and the project (F34 draft).
 * "Zafakturowane poza programem" (menu "…" of a row, edit right) takes an event off, with "Cofnij"; the events taken off
 * wait folded under the table with "Przywróć".
 */
function ToIssueList({ query }: { query: { error: unknown; data?: { events: ToIssueRow[]; outside?: ToIssueRow[] } | undefined } }) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const rows = query.data?.events ?? [];
  const outside = query.data?.outside ?? [];
  const amounts = [...rows, ...outside].some((r) => r.remainingGrossGrosze !== undefined);
  const mayCreate = me ? can(me, INVOICES_RESOURCE, 'create') : false;
  const mayMark = me ? can(me, INVOICES_RESOURCE, 'edit') : false;
  const [taken, setTaken] = useState<ToIssueRow | null>(null);
  const mark = useMutation({
    mutationFn: ({ row, outside: off }: { row: ToIssueRow; outside: boolean }) => api(`/api/invoices/to-issue/${row.eventId}/outside`, { method: off ? 'POST' : 'DELETE' }),
    onSuccess: async (_r, { row, outside: off }) => {
      setTaken(off ? row : null);
      await queryClient.invalidateQueries({ queryKey: [...INVOICES_KEY, 'to-issue'] });
    },
  });
  return (
    <div data-feature-id="invoices.to_issue">
      {query.error ? (
        <p className="alert" role="alert">
          {query.error instanceof ApiError ? query.error.message : 'Nie udało się wczytać wydarzeń do zafakturowania'}
        </p>
      ) : null}
      {mark.error ? (
        <p className="alert" role="alert">
          {mark.error instanceof ApiError ? mark.error.message : 'Nie udało się zmienić listy'}
        </p>
      ) : null}
      {taken && (
        <Notice onClose={() => setTaken(null)}>
          „{taken.eventName}” zdjęte z listy jako zafakturowane poza programem.{' '}
          <button className="btn small" type="button" disabled={mark.isPending} onClick={() => mark.mutate({ row: taken, outside: false })}>
            Cofnij
          </button>
        </Notice>
      )}
      {query.data && rows.length === 0 && <p className="muted">Nie ma nic do wystawienia. Tu trafiają zakończone wydarzenia z zaakceptowanym kosztorysem, których jeszcze nie zafakturowano.</p>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table inv-table" aria-label="Wydarzenia do zafakturowania">
            <thead>
              <tr>
                <th scope="col">Wydarzenie</th>
                <th scope="col" className="wide-only">
                  Klient
                </th>
                <th scope="col">Kosztorys</th>
                {amounts && (
                  <th scope="col" className="num">
                    Do zafakturowania
                  </th>
                )}
                <th scope="col">Projekt faktury</th>
                {mayMark && (
                  <th scope="col">
                    <span className="visually-hidden">Więcej</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <ToIssueItem key={r.eventId} row={r} amounts={amounts} mayCreate={mayCreate} onOutside={mayMark ? () => mark.mutate({ row: r, outside: true }) : undefined} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {outside.length > 0 && (
        <details className="inv-outside">
          <summary>Zafakturowane poza programem ({outside.length})</summary>
          <ul>
            {outside.map((r) => (
              <li key={r.eventId}>
                <span>
                  {r.eventName} <span className="muted">· {formatEventRange(r.startsAt, r.endsAt)}</span>
                </span>
                {mayMark && (
                  <button className="btn small ghost" type="button" aria-label={`Przywróć: ${r.eventName}`} disabled={mark.isPending} onClick={() => mark.mutate({ row: r, outside: false })}>
                    Przywróć
                  </button>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function ToIssueItem({ row: r, amounts, mayCreate, onOutside }: { row: ToIssueRow; amounts: boolean; mayCreate: boolean; onOutside: (() => void) | undefined }) {
  const invoicing = useIssueInvoice(r.eventId);
  return (
    <tr>
      <td>
        <div className="inv-event-name">{r.eventName}</div>
        <div className="muted inv-event-dates">{formatEventRange(r.startsAt, r.endsAt)}</div>
        {r.planShare && <div className="muted small">{planShareNote(r.planShare)}</div>}
        <IssueInvoiceError invoicing={invoicing} />
      </td>
      <td className="wide-only">{r.clientName || <span className="muted">bez klienta</span>}</td>
      <td className="nowrap">v{r.versionNumber}</td>
      {amounts && <td className="num nowrap">{r.remainingGrossGrosze === undefined ? '—' : formatPln(r.remainingGrossGrosze)}</td>}
      <td className="nowrap">
        {r.draftId && r.draftOutdated ? (
          // F34 never opens an outdated draft silently: its "Wystaw fakturę" path offers "Przebuduj z v…"
          <button className="btn small" type="button" disabled={invoicing.pending} onClick={() => invoicing.start(r.versionId, r.versionNumber)}>
            Projekt v{r.draftVersionNumber} – nieaktualny
          </button>
        ) : r.draftId ? (
          <button className="btn small" type="button" onClick={() => invoicing.open(r.draftId!)}>
            Otwórz projekt
          </button>
        ) : mayCreate ? (
          <button className="btn small primary" type="button" disabled={invoicing.pending} onClick={() => invoicing.start(r.versionId, r.versionNumber)}>
            Utwórz projekt
          </button>
        ) : (
          <span className="muted">brak</span>
        )}
      </td>
      {onOutside && (
        <td className="inv-row-menu">
          <RowMenu label={`Więcej działań: ${r.eventName}`} floating actions={[{ label: 'Zafakturowane poza programem', onSelect: onOutside }]} />
        </td>
      )}
    </tr>
  );
}
